import React, { useContext, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as TrashSVG } from "go-core/images/svg/actions/rubbish.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { OrderShipApi } from "../../../../../../../../../../../services/Api/Organization/types";
import { OrderContext } from "../../../pages/Preview";
import OrderShipDetailsModal from "./OrderShipDetailsModal";

interface Props {
	handleUpdate: () => Promise<void>;
}

const OrderShipsCard = ({ handleUpdate }: Props) => {
	const { orderShips, order } = useContext(OrderContext);
	const { t } = useTranslation();
	const [orderShipDetailsToShow, setOrderShipDetailsToShow] = useState<OrderShipApi | undefined>();
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();

	if (!orderShips || orderShips.length === 0) return <></>;

	const getOrderShipTotalQuantity = (orderShip: OrderShipApi) => {
		return orderShip?.items
			?.map((item) => item?.quantity || 0)
			?.reduce((acc, cur) => acc + cur, 0)
			.toFixed(5);
	};

	const handleRemove = async (orderShipId: number) => {
		try {
			await confirmation({
				title: t("lib:go_form.modal_confirm.title"),
				message: t("lib:go_form.modal_confirm.message.remove"),
			});
			await apiOrganization.removeOrderShip(order.id, orderShipId);
			await handleUpdate();
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<Card className="card-box">
			<Card.Body>
				<h5 className="card-title">{t("modules.order.field.ships.title")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th>{t("lib:common.word.number")}</th>
								<th>{t("common.word.reference_number")}</th>
								<th>{t("lib:common.word.date")}</th>
								<th>{t("common.word.quantity")}</th>
								<th className="action" />
							</tr>
						</thead>
						<tbody>
							{orderShips.map((orderShip) => (
								<tr key={orderShip.id}>
									<td>
										<a href="javascript:" onClick={() => setOrderShipDetailsToShow(orderShip)}>
											{orderShip.number}
										</a>
									</td>
									<td>{orderShip.reference_number ?? "-"}</td>
									<td>{FormatDate(orderShip.shipped_at)}</td>
									<td>{getOrderShipTotalQuantity(orderShip)}</td>
									<td className="action">
										{orderShip.id && (
											<Button
												variant="add"
												className="mb-0"
												onClick={() => handleRemove(Number(orderShip.id))}
											>
												<TrashSVG className="trash-icon" />
											</Button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{orderShipDetailsToShow && (
					<OrderShipDetailsModal
						orderShip={orderShipDetailsToShow}
						show={!!orderShipDetailsToShow}
						onHide={() => setOrderShipDetailsToShow(undefined)}
					/>
				)}
			</Card.Body>
		</Card>
	);
};

export default OrderShipsCard;
